import axiosInstance from "./axiosInstance";

function authHeader(token?: string) {
  if (!token || token.trim().length === 0) return {};
  return { Authorization: `Bearer ${token}` };
}

/** OpenAPI `LocationGeoIdsDTO` — POST `/org/locationGeoIds`. */
export type LocationGeoIdsDTO = {
  countryId?: number;
  admin1Id?: number;
  admin2Id?: number;
  cityId?: number;
  postalCode?: number;
  streetAddress1?: string;
  streetAddress2?: string;
  lon?: number;
  lat?: number;
};

export async function saveOrganizationLocationGeoIds(
  token: string,
  payload: LocationGeoIdsDTO
): Promise<void> {
  await axiosInstance.post("/org/locationGeoIds", payload, {
    headers: authHeader(token),
  });
}

/** The store's public contact details (shown to searchers in listing details). */
export interface StoreContact {
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  /** Digits incl. country code, for a wa.me link. */
  whatsapp?: string | null;
}

/** The org's default store as returned by GET /org/store (profile prefill). */
export interface OrgStore {
  name: string | null;
  contact?: StoreContact | null;
}

export async function getOrgStore(token: string): Promise<OrgStore> {
  const resp = await axiosInstance.get<OrgStore>("/org/store", {
    headers: authHeader(token),
  });
  return resp.data;
}

/** Saves the store's public contact details (blank field = channel not offered). */
export async function saveOrganizationContact(
  token: string,
  contact: StoreContact
): Promise<void> {
  await axiosInstance.post("/org/contact", contact, {
    headers: authHeader(token),
  });
}

export type OrgStatus = "ACTIVE" | "CLOSED" | "SUSPENDED";

/** One registered account (organization + its root user). */
export interface OrgAccount {
  orgId: number;
  orgName: string | null;
  status: OrgStatus;
  createdAt: string | null;   // null for orgs predating the field
  closedAt: string | null;
  rootUserId: number | null;
  rootEmail: string | null;
  rootUsername: string | null;
}

/** Admin (ROOT/SYS_ADMIN): all registered organization accounts, newest first. */
export async function listOrgAccounts(token: string): Promise<OrgAccount[]> {
  const resp = await axiosInstance.get<OrgAccount[]>("/orgs/accounts", {
    headers: authHeader(token),
  });
  return resp.data ?? [];
}

/** ORG_ROOT cancels their own organization (password re-confirmed): the org is
 *  closed, member logins are blocked, and the store's listings are hidden. */
export async function closeMyOrganization(token: string, password: string): Promise<void> {
  await axiosInstance.post("/orgs/me/close", { password }, {
    headers: authHeader(token),
  });
}

/** Admin (ROOT/SYS_ADMIN): sets an org ACTIVE (reopen) or SUSPENDED — never CLOSED. */
export async function setOrganizationStatus(
  token: string,
  orgId: number,
  status: OrgStatus
): Promise<void> {
  await axiosInstance.post("/orgs/set-status", { orgId, status }, {
    headers: authHeader(token),
  });
}
